import React, { useEffect, useState } from 'react';

import { Stack, Typography, Link, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Loader } from 'components/Loader';
import { ListItem } from '../components/BlogListItem';
import { useGetAllBlogs } from '../api/getAllBlogs';
import { paths } from 'routes';
import { useLocation } from 'react-router-dom';
import { IconWrapper } from 'components/Icon';
import { LocalizedLink } from 'website/components/LocalizedLink';

export const RecentBlogPosts = ({label, count = 3, currentPostID = null, px = {xs: 4, md: 0}}) => {
  const { t } = useTranslation();
  const resolvedLabel = label ?? t('website:homepage.recentBlogPosts.defaultLabel');
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(null);

  const location = useLocation();
  const websiteContext = location?.state?.websiteContext || 'suppliers';

  const {
    data: { blogs, total },
    refetch: refetchBlogs,
    isFetching,
  } = useGetAllBlogs({
    page: 0,
    limit: count,
    queryOptions: {
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      }
    },
  });

  useRefetch();

  useEffect(() => {
    if (isFetching) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isFetching]);

  if (!blogs || blogs.length === 0) {
    return null;
  }

  return (
      <Stack
        sx={{
          display: "flex",
          gap: 8,
          alignItems: {xs: "center", md: "flex-start"},
          width: 1,
          px,
          boxSizing: "border-box",
        }}
      >
        <Stack gap={2} flexDirection={{xs: "column", md: "row"}}>
          <Typography
            flex={1}
            variant='h3'
            sx={{
              fontSize: {xs: "1.5625rem", md: "2.5rem"},
              fontWeight: 600,
              lineHeight: 1.45,
              letterSpacing: 0,
              wordWrap: 'break-word',
              alignSelf: "center",
              textAlign: {xs: "center", md: "start"},
            }}
          >
            {resolvedLabel}
          </Typography>
        </Stack>
          {isFetching && !loading ? 
          (
            <></>
          ) :
          (!isFetching || (isFetching && loading)) ?
          (
            <>
            {loading && (
              <Loader style={{ marginTop: "20vh"}}/>
            )}
          
            {!loading && (
              <Stack flexDirection={"column"} gap={1} width={1} maxWidth={{xs: 345, md: "none"}} boxSizing={"border-box"}>
                <Stack flexDirection={{xs: "column", md: "row"}} gap={2}>
                  {blogs.length > 0 && (
                    (currentPostID
                      ?
                        blogs.filter((item) => item.blog_id !== Number(currentPostID)).slice(0, count - 1)
                      :
                        blogs
                    ).map((item) => (
                      <ListItem key={item.blog_id} {...item} websiteContext={websiteContext} />
                    ))
                  )}
                </Stack>
                <Box>
                  <Link
                    component={LocalizedLink}
                    to={paths.website.blog}
                    color="primary.light"
                    sx={{height:1}}
                    state={{websiteContext: websiteContext}}
                  >
                    <Stack sx={{justifyContent: {xs: "flex-start", md: "flex-end"}, flexDirection: "row", gap: 1}}>
                      <Typography>
                        {t('website:homepage.recentBlogPosts.seeMore')}
                      </Typography>
                      <IconWrapper
                        icon="arrowRightLong"
                        sx={({ palette }) => ({
                          svg: {
                            color: palette.primary.light,
                            stroke: palette.primary.light,
                            width: 20,
                            height: 20,
                          },
                        })}
                      />
                    </Stack>
                  </Link>
                </Box>
              </Stack>
            )}
            </>
          ) :
          (
            <></>
          )}
        </Stack>
  );

  function useRefetch() {
    useEffect(() => {     
      refetchBlogs();
    }, [page]);
  }
};
